/* Additive renderer proposal. Load after nana-cookalong.js. Does not replace
   data, map/calendar UI, checklists, dialog controls, PDF/image/HTML exports,
   source URLs or existing save/share listeners. All recipe strings use textContent. */
(() => {
  const el = (tag, cls, text) => {
    const n = document.createElement(tag); n.className = cls;
    if (text !== undefined) n.textContent = String(text);
    return n;
  };
  const defaults = {
    title: 'The choices behind this recipe', before: 'Source or common choice',
    after: 'Nana’s choice', effect: 'Cooking effect & cue',
    draft: 'Independent adaptation · untested', tools: 'Share, print or copy a link',
    notes: 'Cooking notes',
    scope: 'Compare the source or common choice, Nana’s version and the cooking effect.',
    missing: 'Cooking effects have not been documented for this change.',
    measured: 'Ingredients', method: 'Let’s cook', checked: 'Source checked',
    dev: 'Cooking expectation · untested', actions: 'Recipe actions', newWindow: 'opens in a new tab'
  };
  function labels(root) {
    const n = root.querySelector('[data-nana-editorial-labels]');
    if (!n) return defaults;
    try { return {...defaults, ...JSON.parse(n.textContent)}; } catch { return defaults; }
  }
  function conversionGuide(recipe, L, id) {
    const pairs = Array.isArray(recipe.conversionPairs) ? recipe.conversionPairs
      : Array.isArray(recipe.conversions) ? recipe.conversions.map(x=>({before:x.from,after:x.to,function:x.why})) : [];
    if (!pairs.length) return null;
    const section=el('section','nre-adaptations');
    const title=el('h3','nre-section-title',L.title);title.id=id;
    section.setAttribute('aria-labelledby',id);section.append(title,el('p','nre-scope',L.scope));
    const list=el('ol','nre-swap-list');
    list.setAttribute('role','list');
    pairs.forEach((pair,i)=>{
      if (!pair || !pair.before || !pair.after) return;
      const row=el('li','nre-swap-row'),terms=el('dl','nre-swap-terms');
      const before=el('div','nre-swap-before'),after=el('div','nre-swap-after'),effect=el('div','nre-swap-effect');
      before.append(el('dt','nre-label',L.before),el('dd','',pair.before));
      after.append(el('dt','nre-label',L.after),el('dd','',pair.after));
      const arrow=el('span','nre-swap-arrow','→');arrow.setAttribute('aria-hidden','true');
      const documented = pair.cookingEffect || recipe.adaptationEffects?.find(x=>x.before===pair.before)?.cookingEffect;
      const explanation=el('dd','');explanation.append(el('p','',documented || pair.function || L.missing));
      if (documented) explanation.append(el('p','nre-effect-status',L.dev));
      if (pair.scope) explanation.append(el('p','nre-pair-scope',pair.scope));
      effect.append(el('dt','nre-label',L.effect),explanation);
      terms.append(before,after,effect);row.append(arrow,terms);list.append(row);
    });
    if (!list.children.length) return null;
    section.append(list);return section;
  }
  function decorate(root) {
    const dialog=root.querySelector('[data-nt-dialog]');
    if (!dialog?.open) return;
    const body=root.querySelector('[data-nt-detail] .nt-detail-body'),cook=body?.querySelector('[data-nt-cookalong]');
    if (!body || !cook || body.dataset.nanaEditorialReady) return;
    const day=Number(body.querySelector('.nt-date')?.textContent.match(/\d+/)?.[0]);
    const recipe=window.NanaCookAlong?.[day];
    if (!recipe?.ingredients || !recipe?.steps) return;
    const L=labels(root),ingredients=cook.querySelector('.nt-cook-ingredients'),steps=cook.querySelector('.nt-cook-steps');
    if (!ingredients || !steps) return;
    body.dataset.nanaEditorialReady=String(day);body.classList.add('nre-detail');
    const heading=body.querySelector('h2'),country=body.querySelector('.nt-country'),date=body.querySelector('.nt-date');
    const lede=el('header','nre-lede');
    if (heading) {
      if (recipe.displayTitle) heading.textContent=recipe.displayTitle;
      lede.append(heading);
    }
    if (country) lede.append(country);
    const meta=el('div','nre-meta');
    if (recipe.yield) meta.append(el('span','nre-yield',recipe.yield));
    meta.append(el('span','nre-draft',L.draft));
    if (recipe.checkedDate) meta.append(el('span','nre-checked',L.checked+' '+recipe.checkedDate));
    lede.append(meta);date ? date.after(lede) : body.prepend(lede);
    const photo=body.querySelector('.nt-food-photo'),photoNote=body.querySelector('.nt-photo-note');
    if (photo) {
      const figure=el('figure','nre-hero');figure.append(photo);
      if (photoNote) { const caption=el('figcaption','nre-photo-note',photoNote.textContent);photoNote.remove();figure.append(caption); }
      lede.after(figure);
    }
    const tools=body.querySelector('[data-nt-phone-tools]'),actions=tools?.querySelector('.nt-phone-actions');
    if (actions) {
      actions.setAttribute('role','group');actions.setAttribute('aria-label',L.actions);
      const extra=[...actions.children].filter(n=>!n.classList.contains('nt-phone-primary') && !n.hasAttribute('aria-pressed'));
      if (extra.length) {
        const drawer=el('details','nre-extra-tools'),summary=el('summary','',L.tools),box=el('div','nre-extra-actions');
        box.append(...extra);drawer.append(summary,box);tools.append(drawer);
      }
      const cookButton=actions.querySelector('[aria-pressed]');if(cookButton)actions.prepend(cookButton);
      tools.classList.add('nre-recipe-tools');
    }
    const guide=conversionGuide(recipe,L,root.id+'-adaptations-'+day);
    if (guide) {
      const credit=cook.querySelector('.nt-cook-credit');
      if (credit) {
        credit.classList.add('nre-guide-credit');
        credit.querySelectorAll('a[target="_blank"]').forEach(a=>a.setAttribute('aria-label',a.textContent+' ('+L.newWindow+')'));
        guide.insertBefore(credit,guide.querySelector('.nre-swap-list'));
      }
      cook.before(guide);
    }
    const recipeBody=el('div','nre-recipe-body'),pantry=el('section','nre-ingredients'),method=el('section','nre-method');
    const hs=[...cook.querySelectorAll(':scope > h3')];
    pantry.append(hs[0] || el('h3','',L.measured),ingredients);
    method.append(hs[1] || el('h3','',L.method),steps);
    cook.querySelector(':scope > .nt-cook-yield')?.remove(); // Yield is visible in the lede.
    recipeBody.append(pantry,method);cook.prepend(recipeBody);
    if (recipe.safety) { const safety=el('section','nre-safety'); safety.append(el('h3','nre-section-title','Cooking & storage'),el('p','',recipe.safety));cook.append(safety); }
    const note=cook.querySelector(':scope > .nt-cook-note');
    if (note && recipe.safety) { const disclosure=el('details','nre-development-notes');disclosure.append(el('summary','',L.notes),note);cook.append(disclosure); }
    // Existing attribution, safety text, links and external handlers remain live.
  }
  function init(scope=document) {
    scope.querySelectorAll('.nana-tasting').forEach(root=>{
      if(root.dataset.nanaEditorialObserver) return;
      const detail=root.querySelector('[data-nt-detail]'),dialog=root.querySelector('[data-nt-dialog]');
      if(!detail || !dialog) return;
      root.dataset.nanaEditorialObserver='true';
      const observer=new MutationObserver(()=>decorate(root));
      observer.observe(detail,{childList:true,subtree:true});
      const dialogObserver=new MutationObserver(()=>decorate(root));
      dialogObserver.observe(dialog,{attributes:true,attributeFilter:['open']});
      root.addEventListener('nana:cookalong-ready',()=>decorate(root));decorate(root);
    });
  }
  window.NanaRecipeEditorial={init,conversionGuide,decorate};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>init(),{once:true});else init();
  document.addEventListener('shopify:section:load',event=>init(event.target));
})();
